import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { cancelHotelReservation, getHotelReservationCancelPenalty } from '../../../api/endpoints/hotel';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import type { HotelFlowState } from '../flowConfig';
import { HotelCancelPenaltyBreakdown } from './HotelCancelPenaltyBreakdown';
import { offerRoomName } from './ResultsStep';

export function DocumentStep() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const { enqueueSnackbar } = useSnackbar();
  const { reservation, payment, selectedOffer, contact } = flow.state;
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  // İptal cezası ön-sorgusu — diyalog açılınca `Hotel/GetReservationCancelPenalty`,
  // `Hotel/GetReservationCancel`'den ÖNCE. Hata verirse (`retry: false`) diyalog ceza
  // kırılımı olmadan + küçük bir not ile gösterilir; iptal akışı bloke olmaz.
  const penaltyQuery = useQuery({
    queryKey: ['hotelCancelPenalty', reservation?.rezId, selectedOffer?.currencyType],
    queryFn: () =>
      getHotelReservationCancelPenalty(reservation?.rezId as string, {
        currencyType: selectedOffer?.currencyType,
      }),
    enabled: cancelOpen && !!reservation?.rezId,
    retry: false,
    staleTime: 30 * 1000,
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelHotelReservation(reservation?.rezId ?? '', reason),
    onSuccess: () => {
      enqueueSnackbar('İptal talebi gönderildi.', { variant: 'success' });
      setCancelOpen(false);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İptal işlemi başarısız.', { variant: 'error' }),
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Voucher
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          {reservation?.status && <Chip size="small" color="success" label="Rezervasyon Onaylandı" />}
          {reservation?.rezId && <Chip size="small" label={`Rez No: ${reservation.rezId}`} />}
          {reservation?.rezRefId && <Chip size="small" label={`Ref: ${reservation.rezRefId}`} />}
        </Stack>

        {reservation?.responseMessage && (
          <Alert severity={reservation.status ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {reservation.responseMessage}
          </Alert>
        )}

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2">Otel: {selectedOffer?.hotelName ?? '—'}</Typography>
          <Typography variant="body2">
            Oda: {(selectedOffer && offerRoomName(selectedOffer)) ?? '—'} / {selectedOffer?.rateName ?? '—'}
          </Typography>
          <Typography variant="body2">
            Misafir: {contact?.name} {contact?.lastName}
          </Typography>
        </Stack>

        {payment && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Ödeme
            </Typography>
            {payment.hasError && <Alert severity="error">Ödeme tamamlanamadı.</Alert>}
            {!payment.hasError && <Alert severity="success">Ödeme isteği başarıyla gönderildi.</Alert>}
            {payment.continueUrl && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                3D Secure devam etmek için:{' '}
                <Link href={payment.continueUrl} target="_blank" rel="noopener">
                  {payment.continueUrl}
                </Link>
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Button onClick={flow.reset}>Baştan başla</Button>
          <Button color="error" variant="outlined" disabled={!reservation?.rezId} onClick={() => setCancelOpen(true)}>
            Rezervasyonu İptal Et
          </Button>
        </Box>
      </CardContent>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rezervasyonu iptal et</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {penaltyQuery.isFetching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {penaltyQuery.isError && (
              <Alert severity="warning">Ceza bilgisi alınamadı.</Alert>
            )}
            {penaltyQuery.data && <HotelCancelPenaltyBreakdown body={penaltyQuery.data.body} />}
            <Typography variant="body2">Bu işlem geri alınamaz.</Typography>
          </Stack>
          <TextField
            label="İptal nedeni"
            size="small"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={cancelMutation.isPending}>
            Vazgeç
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending || !reason}
            onClick={() => cancelMutation.mutate()}
          >
            İptal Et
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
